import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMediaQuery } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { updateStoresOpenState } from '@as-react/common-utils';
import { useIsClient } from '@yonderland/andes-react';
import { Box, Stack } from '@yonderland/andes-react';
import { useEffect, useRef, useState } from 'react';
import OsmAPI from '../../../api/open-street-map/OsmApi';
import { MAX_SCREEN_WIDTH_MOBILE_LARGE, MIN_SCREEN_WIDTH_TABLET_SMALL, MAX_SCREEN_WIDTH_TABLET_SMALL, } from '../../../constants/ScreenWidthConstants';
import { calculateDistanceOSM } from '../../../util/LocationUtil';
import { getQuerystringByName, setQuerystringParameter, removeQuerystringParameter } from '../../../util/URLParamUtil';
import { Map, SEARCH_LOCATION_ID } from '../../product-detail/store-finder-popup/map/Map';
import StoreNearbyStores from '../StoreNearbyStores';
import { MarkerPopupContent } from './MarkerPopupContent';
import { StoreLocatorFilter } from './StoreLocatorFilter';
const FILTER_ERROR = {
    NO_SEARCH_RESULT: 'store.error.no.store.found',
    NO_FILTER_RESULT: 'store.error.no.store.for.filter.found',
};
const LOCATION_PARAM_KEY = 'location';
const NEARBY_STORES_AMOUNT = 6;
const MARKER_ICON_SIZE = [60, 45];
const mapContainerStyles = (isMobile, isMobileLarge) => ({
    width: '100%',
    height: isMobile ? '250px' : isMobileLarge ? '400px' : '500px',
});
const getNearbyStoresForLocation = (location, storesData, storesAmount) => {
    const storesWithDistance = storesData.map(store => (Object.assign(Object.assign({}, store), { distance: calculateDistanceOSM(location, {
            latitude: Number(store.geographic.latitude),
            longitude: Number(store.geographic.longitude),
        }) })));
    const orderedByDistanceStores = storesWithDistance.sort((a, b) => a.distance - b.distance);
    return orderedByDistanceStores.slice(0, storesAmount);
};
const getLocationQuerystring = () => typeof window !== 'undefined' ? getQuerystringByName(window.location.search, LOCATION_PARAM_KEY) : '';
const StoreLocator = ({ showAllStores, openAfterTime, storeTimeNotation, storeInformation, }) => {
    const { defaultRequestParams, countryCodes, root, thirdparty } = useAemConfig();
    const mapsConfig = thirdparty.googleMaps;
    const locationQueryString = useRef(getLocationQuerystring());
    const { current: stores } = useRef(updateStoresOpenState(storeInformation, openAfterTime));
    const [filteredStores, setFilteredStores] = useState(stores);
    const [nearbyStores, setNearbyStores] = useState([]);
    const [searchLocation, setSearchLocation] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterError, setFilterError] = useState(null);
    const isClient = useIsClient();
    const isMobile = useMediaQuery(`(max-width: ${MAX_SCREEN_WIDTH_MOBILE_LARGE}px)`);
    const isMobileLarge = useMediaQuery(`(min-width: ${MIN_SCREEN_WIDTH_TABLET_SMALL}px) and (max-width: ${MAX_SCREEN_WIDTH_TABLET_SMALL}px)`);
    const resetFiltersState = () => {
        setSearchLocation(null);
        setSearchTerm('');
        setNearbyStores([]);
    };
    const getNearbyStoresInformation = (location, storesData) => __awaiter(void 0, void 0, void 0, function* () {
        if (location) {
            if (location === searchTerm)
                return;
            try {
                const response = yield OsmAPI.getLocationFromOSM(location, countryCodes, 1);
                if (response === null || response === void 0 ? void 0 : response.length) {
                    const _searchLocation = { latitude: Number(response[0].lat), longitude: Number(response[0].lon) };
                    const _nearByStores = getNearbyStoresForLocation(_searchLocation, storesData, NEARBY_STORES_AMOUNT);
                    setQuerystringParameter(LOCATION_PARAM_KEY, location);
                    setSearchLocation(_searchLocation);
                    setNearbyStores(_nearByStores);
                }
                else {
                    removeQuerystringParameter(LOCATION_PARAM_KEY);
                    setSearchLocation(null);
                    setNearbyStores([]);
                }
                setFilteredStores(storesData);
                setSearchTerm(location);
            }
            catch (_a) { }
        }
        else {
            removeQuerystringParameter(LOCATION_PARAM_KEY);
            setFilteredStores(storesData);
            resetFiltersState();
        }
    });
    const handleFiltersChange = (filters) => {
        removeQuerystringParameter(LOCATION_PARAM_KEY);
        resetFiltersState();
        const enabledFiltersEntries = Object.entries(filters).filter(entry => entry[1]);
        if (enabledFiltersEntries.length) {
            const _filteredStores = stores.filter(store => {
                let shouldBeDisplayed = true;
                enabledFiltersEntries.forEach(([key, value]) => {
                    if (store[key] !== value) {
                        shouldBeDisplayed = false;
                    }
                });
                return shouldBeDisplayed;
            });
            setFilteredStores(_filteredStores);
        }
        else {
            setFilteredStores(stores);
        }
    };
    const handleSearchSubmit = (location) => getNearbyStoresInformation(location, stores);
    const getMarker = (store) => ({
        id: String(store.code),
        latitude: Number(store.geographic.latitude),
        longitude: Number(store.geographic.longitude),
        storeMainWebShop: defaultRequestParams.mainWebshop,
        popupContent: (_jsx(MarkerPopupContent, { description: store.description, phoneNumber: store.phoneNumber, address: store.address, storeUrl: `${root}${store.relativeStorePagePath}.html` })),
    });
    const [markers, focusOnMarkers] = (() => {
        const markersData = filteredStores.map(store => getMarker(store));
        const focusOnMarkersData = (nearbyStores.length ? nearbyStores : stores).map(store => getMarker(store));
        if ((searchLocation || !showAllStores) && focusOnMarkersData.length > 0) {
            focusOnMarkersData[0].openPopup = true; // open the nearest store popup when search or only one store configured
        }
        if (searchLocation) {
            markersData.push({
                id: SEARCH_LOCATION_ID,
                latitude: Number(searchLocation.latitude),
                longitude: Number(searchLocation.longitude),
            });
        }
        return [markersData, focusOnMarkersData];
    })();
    useEffect(() => {
        if (showAllStores && locationQueryString.current) {
            getNearbyStoresInformation(locationQueryString.current, stores);
        }
    }, []);
    useEffect(() => {
        if (!stores.length)
            return;
        if (searchTerm) {
            setFilterError(nearbyStores.length ? null : FILTER_ERROR.NO_SEARCH_RESULT);
        }
        else {
            const hasVisibleMarkers = Boolean(filteredStores.length);
            setFilterError(hasVisibleMarkers ? null : FILTER_ERROR.NO_FILTER_RESULT);
        }
    }, [stores, filteredStores, nearbyStores, searchTerm]);
    if (!isClient)
        return null;
    return (_jsxs(Stack, { testId: "store-locator", direction: "col", gap: "4", children: [showAllStores && (_jsx(StoreLocatorFilter, { defaultSearch: locationQueryString.current, errorMessage: filterError, openAfterTime: openAfterTime, storeTimeNotation: storeTimeNotation, onFiltersChange: handleFiltersChange, onSearchSubmit: handleSearchSubmit })), _jsx(Box, { marginInline: { base: '-4', lg: '0' }, children: _jsx(Map, { active: true, isOpen: false, keyboard: true, markers: markers, focusOnMarkers: focusOnMarkers, showFasciaIcons: true, noMarkersAnimation: true, containerStyles: mapContainerStyles(isMobile, isMobileLarge), markerIconConfigOverrides: {
                        fasciaIcon: {
                            iconSize: MARKER_ICON_SIZE,
                            iconAnchor: null,
                            iconUrl: '/content/dam/{mainWebShop}/google-maps-icons/default.png',
                        },
                    }, defaultBounds: [
                        [
                            Number(mapsConfig.defaultBounds.northEastCorner.lat),
                            Number(mapsConfig.defaultBounds.northEastCorner.lng),
                        ],
                        [
                            Number(mapsConfig.defaultBounds.southWestCorner.lat),
                            Number(mapsConfig.defaultBounds.southWestCorner.lng),
                        ],
                    ] }) }), Boolean(showAllStores && nearbyStores.length) && _jsx(StoreNearbyStores, { openedStores: nearbyStores })] }));
};
export default StoreLocator;
